//
// # Logger System
//
// A barebones logging interface that separates library logging from application configuration.
// Inspired by the Rust tracing crate - libraries are "producers" and applications are "consumers".
//

// Log levels with numeric values for comparison
export const LogLevel = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
} as const;

export type LogLevel = (typeof LogLevel)[keyof typeof LogLevel];

export interface Logger {
  warn(...args: Array<unknown>): void;
  log(...args: Array<unknown>): void;
  error(...args: Array<unknown>): void;
  debug(...args: Array<unknown>): void;
  setLevel(level: LogLevel): void;
}

// Console-based logger for web/client environments
export class ConsoleLogger implements Logger {
  private level: LogLevel = LogLevel.INFO;

  setLevel(level: LogLevel): void {
    // Validate that the level is a valid LogLevel value
    const validLevels = [
      LogLevel.ERROR,
      LogLevel.WARN,
      LogLevel.INFO,
      LogLevel.DEBUG,
    ];
    if (!validLevels.includes(level)) {
      console.error("Invalid log level provided:", level);
      return;
    }
    this.level = level;
  }

  warn(...args: Array<unknown>): void {
    if (this.level >= LogLevel.WARN) {
      console.warn(...args);
    }
  }

  log(...args: Array<unknown>): void {
    if (this.level >= LogLevel.INFO) {
      console.log(...args);
    }
  }

  error(...args: Array<unknown>): void {
    if (this.level >= LogLevel.ERROR) {
      console.error(...args);
    }
  }

  debug(...args: Array<unknown>): void {
    if (this.level >= LogLevel.DEBUG) {
      console.debug(...args);
    }
  }
}

// Global logger instance - hidden behind accessor methods
let _THE_LOGGER: Logger = new ConsoleLogger();

// Public API for the logging system
export const logger = {
  // Application configuration method - called at startup to set the global logger
  set(loggerInstance: Logger): void {
    _THE_LOGGER = loggerInstance;
  },

  // Set log level on current logger
  setLevel(level: LogLevel): void {
    _THE_LOGGER.setLevel(level);
  },

  // Library logging methods - delegate to the current global logger
  warn(...args: Array<unknown>): void {
    _THE_LOGGER.warn(...args);
  },

  log(...args: Array<unknown>): void {
    _THE_LOGGER.log(...args);
  },

  error(...args: Array<unknown>): void {
    _THE_LOGGER.error(...args);
  },

  debug(...args: Array<unknown>): void {
    _THE_LOGGER.debug(...args);
  },
};
